import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import * as customersApi from '@/api/customers'
import { useAuth } from '@/auth/AuthContext'
import { CategoryIcon, CATEGORY_ICON_OPTIONS } from '@/components/CategoryIcon'
import {
  FieldEditor,
  fieldsFromColumns,
  toApiFields,
} from '@/components/FieldEditor'

const DEFAULT_TABLE_COLUMNS = [
  { label: 'Name', input_type: 'text' },
  { label: 'URL', input_type: 'url' },
  { label: 'Password', input_type: 'secret' },
]

function defaultTableFields() {
  return fieldsFromColumns(DEFAULT_TABLE_COLUMNS)
}
import { SettingsShell } from '@/components/SettingsShell'
import {
  childrenOf,
  orderedCategoryList,
  parentOptions,
} from '@/lib/categories'
import {
  getRememberedSettingsCustomer,
  rememberSettingsCustomer,
} from '@/settingsContext'

const KIND_LABELS = {
  rich: 'Rich text',
  table: 'Table',
  devices: 'Devices (Dash)',
}

function IconPicker({ value, onChange, disabled }) {
  const current = value || 'document'
  return (
    <label className="block text-sm">
      <span className="text-brand-muted">Icon</span>
      <div className="mt-1 flex items-center gap-2">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-slate-300 bg-white text-brand">
          <CategoryIcon name={current} className="h-4 w-4" />
        </span>
        <select
          value={current}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="min-w-0 flex-1 rounded border border-slate-300 bg-white px-2 py-2 text-sm"
        >
          {CATEGORY_ICON_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    </label>
  )
}

export function ManageCategories() {
  const { isAdmin } = useAuth()
  const [customerOptions, setCustomerOptions] = useState([])
  const [customerId, setCustomerId] = useState(() => getRememberedSettingsCustomer())
  const [customer, setCustomer] = useState(null)
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [newTitle, setNewTitle] = useState('')
  const [newKind, setNewKind] = useState('rich')
  const [newIcon, setNewIcon] = useState('document')
  const [newColumns, setNewColumns] = useState(() => defaultTableFields())
  const [editingSlug, setEditingSlug] = useState(null)
  const [editTitle, setEditTitle] = useState('')
  const [editIcon, setEditIcon] = useState('document')
  const [editColumns, setEditColumns] = useState([])
  const [columnsSlug, setColumnsSlug] = useState(null)
  const [editIsSub, setEditIsSub] = useState(false)
  const [editParentSlug, setEditParentSlug] = useState('')
  const [newIsSub, setNewIsSub] = useState(false)
  const [newParentSlug, setNewParentSlug] = useState('')

  const displayCategories = useMemo(
    () => orderedCategoryList(categories),
    [categories],
  )
  const newParentChoices = useMemo(
    () => parentOptions(categories),
    [categories],
  )

  useEffect(() => {
    if (!isAdmin) return undefined
    let cancelled = false
    async function loadCustomers() {
      try {
        const list = await customersApi.listCustomers()
        if (cancelled) return
        const active = list
          .filter((c) => c.is_active !== false)
          .sort((a, b) => a.name.localeCompare(b.name))
        setCustomerOptions(active)
        if (!customerId && active[0]) {
          setCustomerId(String(active[0].id))
          rememberSettingsCustomer(active[0].id)
        }
      } catch {
        if (!cancelled) setCustomerOptions([])
      }
    }
    loadCustomers()
    return () => {
      cancelled = true
    }
  }, [isAdmin, customerId])

  const loadCategories = useCallback(async (id) => {
    if (!id) {
      setCustomer(null)
      setCategories([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError('')
    try {
      const [c, cats] = await Promise.all([
        customersApi.getCustomer(id),
        customersApi.listCategories(id),
      ])
      setCustomer(c)
      setCategories(cats)
      rememberSettingsCustomer(id, c.name)
    } catch (err) {
      setCustomer(null)
      setCategories([])
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not load categories.',
      )
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!isAdmin) return
    loadCategories(customerId)
  }, [isAdmin, customerId, loadCategories])

  if (!isAdmin) {
    return <Navigate to="/" replace />
  }

  function fail(err, fallback) {
    setError(
      typeof err.response?.data?.detail === 'string'
        ? err.response.data.detail
        : fallback,
    )
  }

  function selectCustomer(nextId) {
    const match = customerOptions.find((c) => String(c.id) === String(nextId))
    setCustomerId(String(nextId))
    rememberSettingsCustomer(nextId, match?.name)
    setEditingSlug(null)
    setEditTitle('')
    setEditIcon('document')
    setColumnsSlug(null)
    setEditColumns([])
    setEditIsSub(false)
    setEditParentSlug('')
    setNewIsSub(false)
    setNewParentSlug('')
  }

  async function handleAdd(e) {
    e.preventDefault()
    const title = newTitle.trim()
    if (!title || busy || !customerId) return
    let columns = newKind === 'table' ? toApiFields(newColumns) : undefined
    if (newKind === 'table' && (!columns || columns.length === 0)) {
      columns = DEFAULT_TABLE_COLUMNS
    }
    if (newIsSub && !newParentSlug) {
      setError('Choose a parent category for the sub-category.')
      return
    }
    setBusy(true)
    setError('')
    try {
      const created = await customersApi.createCategory(customerId, {
        title,
        kind: newKind,
        icon: newIcon,
        columns,
        parent_slug: newIsSub ? newParentSlug : undefined,
      })
      setCategories((prev) => [...prev, created])
      setNewTitle('')
      setNewKind('rich')
      setNewIcon('document')
      setNewColumns(defaultTableFields())
      setNewIsSub(false)
      setNewParentSlug('')
    } catch (err) {
      fail(err, 'Could not create category.')
    } finally {
      setBusy(false)
    }
  }

  async function handleSaveEdit(slug) {
    const title = editTitle.trim()
    if (!title || busy || !customerId) return
    if (editIsSub && !editParentSlug) {
      setError('Choose a parent category for the sub-category.')
      return
    }
    const cat = categories.find((c) => c.slug === slug)
    const payload = {
      title,
      icon: editIcon,
      parent_slug: editIsSub ? editParentSlug : null,
    }
    if (cat && (cat.kind === 'table' || cat.kind === 'rich') && columnsSlug === slug) {
      const columns = toApiFields(editColumns)
      if (cat.kind === 'table' && columns.length === 0) {
        setError('Tables need at least one field name.')
        return
      }
      payload.columns = columns
    }
    setBusy(true)
    setError('')
    try {
      const updated = await customersApi.updateCategory(customerId, slug, payload)
      setCategories((prev) =>
        prev.map((c) => (c.slug === slug ? { ...c, ...updated } : c)),
      )
      setEditingSlug(null)
      setEditTitle('')
      setEditIcon('document')
      setColumnsSlug(null)
      setEditColumns([])
      setEditIsSub(false)
      setEditParentSlug('')
    } catch (err) {
      fail(err, 'Could not save category.')
    } finally {
      setBusy(false)
    }
  }

  async function moveCategory(slug, direction) {
    const cat = categories.find((c) => c.slug === slug)
    if (!cat || !customerId || busy) return
    const parentKey = cat.parent_slug || null
    const siblings = categories
      .filter((c) => (c.parent_slug || null) === parentKey)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    const idx = siblings.findIndex((c) => c.slug === slug)
    const target = idx + direction
    if (idx < 0 || target < 0 || target >= siblings.length) return
    const nextSiblings = [...siblings]
    const tmp = nextSiblings[idx]
    nextSiblings[idx] = nextSiblings[target]
    nextSiblings[target] = tmp

    const tops = categories
      .filter((c) => !c.parent_slug)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    const slugs = []
    if (parentKey === null) {
      for (const t of nextSiblings) {
        slugs.push(t.slug)
        for (const child of childrenOf(categories, t.slug)) {
          slugs.push(child.slug)
        }
      }
    } else {
      for (const t of tops) {
        slugs.push(t.slug)
        if (t.slug === parentKey) {
          for (const child of nextSiblings) slugs.push(child.slug)
        } else {
          for (const child of childrenOf(categories, t.slug)) {
            slugs.push(child.slug)
          }
        }
      }
    }
    for (const c of categories) {
      if (!slugs.includes(c.slug)) slugs.push(c.slug)
    }

    setBusy(true)
    setError('')
    try {
      const ordered = await customersApi.reorderCategories(customerId, slugs)
      setCategories(ordered)
    } catch (err) {
      fail(err, 'Could not reorder categories.')
      await loadCategories(customerId)
    } finally {
      setBusy(false)
    }
  }

  async function sortCategoriesAlphabetically() {
    if (!customerId || busy || categories.length < 2) return
    const byTitle = (a, b) =>
      String(a.title || '').localeCompare(String(b.title || ''), 'en', {
        sensitivity: 'base',
        numeric: true,
      })
    const tops = categories
      .filter((c) => !c.parent_slug)
      .slice()
      .sort(byTitle)
    const slugs = []
    for (const top of tops) {
      slugs.push(top.slug)
      const children = childrenOf(categories, top.slug).slice().sort(byTitle)
      for (const child of children) slugs.push(child.slug)
    }
    for (const c of categories) {
      if (!slugs.includes(c.slug)) slugs.push(c.slug)
    }
    setBusy(true)
    setError('')
    try {
      const ordered = await customersApi.reorderCategories(customerId, slugs)
      setCategories(ordered)
    } catch (err) {
      fail(err, 'Could not sort categories.')
      await loadCategories(customerId)
    } finally {
      setBusy(false)
    }
  }

  async function handleDelete(slug, title) {
    if (
      !window.confirm(
        `Delete "${title}"? All content in this category will be removed.`,
      )
    ) {
      return
    }
    setBusy(true)
    setError('')
    try {
      await customersApi.deleteCategory(customerId, slug)
      setCategories((prev) => prev.filter((c) => c.slug !== slug))
    } catch (err) {
      fail(err, 'Could not delete category.')
    } finally {
      setBusy(false)
    }
  }

  function startEdit(cat) {
    setEditingSlug(cat.slug)
    setEditTitle(cat.title)
    setEditIcon(cat.icon || 'document')
    setEditIsSub(Boolean(cat.parent_slug))
    setEditParentSlug(cat.parent_slug || '')
    if (cat.kind === 'table' || cat.kind === 'rich') {
      setColumnsSlug(cat.slug)
      setEditColumns(fieldsFromColumns(cat.columns))
    } else {
      setColumnsSlug(null)
      setEditColumns([])
    }
  }

  return (
    <SettingsShell
      title="Categories"
      customerId={customerId || undefined}
      customerName={customer?.name}
    >
      <div className="space-y-6">
            <div className="rounded-xl border border-slate-200 bg-white/90 p-8 shadow-sm">
              <h1 className="font-display text-4xl tracking-tight text-brand">
                Manage categories
              </h1>
              <p className="mt-2 text-sm text-brand-muted">
                Add, rename, reorder or remove sidebar categories. Tick
                <span className="font-medium text-brand"> Sub-category</span> to nest
                under a parent in the menu. For tables, set your own field names
                (and optionally type).
              </p>

              <label className="mt-6 block max-w-md text-sm">
                <span className="font-medium text-brand">Editing categories for</span>
                <select
                  value={customerId || ''}
                  onChange={(e) => selectCustomer(e.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-brand outline-none focus:border-brand focus:ring-1 focus:ring-brand"
                >
                  <option value="" disabled>
                    Select a customer…
                  </option>
                  {customerOptions.map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>

              {customer && (
                <p className="mt-3 text-sm text-brand">
                  You are editing the knowledge-base sidebar for{' '}
                  <strong>{customer.name}</strong>.
                  {' '}
                  <Link
                    to={`/customers/${customer.id}`}
                    className="underline"
                  >
                    Open knowledge base
                  </Link>
                </p>
              )}
            </div>

            {error && (
              <p className="text-sm text-red-700" role="alert">
                {error}
              </p>
            )}

            {!customerId ? (
              <p className="text-brand-muted">
                Choose a customer above to manage their categories.
              </p>
            ) : loading ? (
              <p className="text-brand-muted">Loading…</p>
            ) : (
              <div className="rounded-xl border border-slate-200 bg-white/90 p-6 shadow-sm sm:p-8">
                {displayCategories.length >= 2 && (
                  <div className="mb-4 flex flex-wrap items-center justify-end gap-2">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={sortCategoriesAlphabetically}
                      className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-brand hover:bg-slate-50 disabled:opacity-50"
                    >
                      Sort A–Z
                    </button>
                  </div>
                )}
                <div className="space-y-3">
                  {displayCategories.length === 0 && (
                    <p className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-5 py-8 text-center text-sm text-brand-muted">
                      No categories yet. Add one below.
                    </p>
                  )}
                  {displayCategories.map((cat) => {
                    const parentKey = cat.parent_slug || null
                    const siblings = categories
                      .filter((c) => (c.parent_slug || null) === parentKey)
                      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
                    const sibIndex = siblings.findIndex((c) => c.slug === cat.slug)
                    const editParents = parentOptions(categories, cat.slug)
                    const hasChildren = childrenOf(categories, cat.slug).length > 0
                    return (
                    <div
                      key={cat.slug}
                      className={[
                        'w-full rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3 shadow-sm sm:px-5 sm:py-4',
                        cat.parent_slug ? 'ml-4 border-l-4 border-l-slate-300 sm:ml-6' : '',
                      ].join(' ')}
                    >
                      {editingSlug === cat.slug ? (
                        <div className="space-y-3">
                          <input
                            autoFocus
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                          />
                          <IconPicker
                            value={editIcon}
                            onChange={setEditIcon}
                            disabled={busy}
                          />
                          <label className="flex items-center gap-2 text-sm text-brand">
                            <input
                              type="checkbox"
                              checked={editIsSub}
                              disabled={busy || hasChildren}
                              onChange={(e) => {
                                const on = e.target.checked
                                setEditIsSub(on)
                                if (!on) setEditParentSlug('')
                                else if (!editParentSlug && editParents[0]) {
                                  setEditParentSlug(editParents[0].slug)
                                }
                              }}
                            />
                            Sub-category in the menu
                          </label>
                          {hasChildren && (
                            <p className="text-xs text-brand-muted">
                              Clear or move its sub-categories before nesting this one.
                            </p>
                          )}
                          {editIsSub && (
                            <label className="block text-sm">
                              <span className="text-brand-muted">Parent category</span>
                              <select
                                value={editParentSlug}
                                onChange={(e) => setEditParentSlug(e.target.value)}
                                disabled={busy}
                                className="mt-1 w-full rounded border border-slate-300 bg-white px-3 py-2"
                              >
                                <option value="">Select parent…</option>
                                {editParents.map((p) => (
                                  <option key={p.slug} value={p.slug}>
                                    {p.title}
                                  </option>
                                ))}
                              </select>
                            </label>
                          )}
                          { (cat.kind === 'table' || cat.kind === 'rich') && (
                            <FieldEditor
                              columns={editColumns}
                              onChange={setEditColumns}
                              disabled={busy}
                            />
                          )}
                          {cat.kind === 'rich' && (
                            <p className="text-xs text-brand-muted">
                              Add field names to attach a data table under the rich-text
                              notes. Leave fields empty for notes only.
                            </p>
                          )}
                          <div className="flex gap-2">
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => handleSaveEdit(cat.slug)}
                              className="rounded-md bg-brand px-3 py-1.5 text-xs text-white hover:bg-slate-800"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              className="text-xs text-brand-muted"
                              onClick={() => {
                                setEditingSlug(null)
                                setEditTitle('')
                                setEditIcon('document')
                                setColumnsSlug(null)
                                setEditColumns([])
                                setEditIsSub(false)
                                setEditParentSlug('')
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 bg-white text-brand">
                                <CategoryIcon name={cat.icon || 'document'} />
                              </span>
                              <span className="font-medium text-brand">{cat.title}</span>
                              <span className="rounded-full bg-white px-2.5 py-0.5 text-xs font-medium text-brand-muted ring-1 ring-slate-200">
                                {KIND_LABELS[cat.kind] || cat.kind}
                              </span>
                              {cat.parent_slug && (
                                <span className="rounded-full bg-slate-200/80 px-2.5 py-0.5 text-xs font-medium text-brand-muted">
                                  Sub-category
                                </span>
                              )}
                              {cat.hidden_from_customer && (
                                <span className="text-xs text-amber-700">Hidden</span>
                              )}
                            </div>
                            {(cat.kind === 'table' || cat.kind === 'rich') &&
                              cat.columns?.length > 0 && (
                              <p className="mt-1 text-xs text-brand-muted">
                                Fields: {cat.columns.map((c) => c.label).join(', ')}
                              </p>
                            )}
                          </div>
                          <div className="flex shrink-0 flex-wrap items-center gap-2 sm:gap-3">
                            <div className="flex gap-1">
                              <button
                                type="button"
                                disabled={busy || sibIndex <= 0}
                                onClick={() => moveCategory(cat.slug, -1)}
                                className="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs disabled:opacity-40"
                              >
                                Up
                              </button>
                              <button
                                type="button"
                                disabled={busy || sibIndex < 0 || sibIndex >= siblings.length - 1}
                                onClick={() => moveCategory(cat.slug, 1)}
                                className="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs disabled:opacity-40"
                              >
                                Down
                              </button>
                            </div>
                            <button
                              type="button"
                              className="text-xs font-medium text-brand hover:underline"
                              onClick={() => startEdit(cat)}
                            >
                              {cat.kind === 'devices' ? 'Rename' : 'Edit'}
                            </button>
                            <button
                              type="button"
                              disabled={busy}
                              className="text-xs font-medium text-red-700 hover:underline disabled:opacity-40"
                              onClick={() => handleDelete(cat.slug, cat.title)}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    )
                  })}
                </div>

                <form
                  onSubmit={handleAdd}
                  className="mt-8 space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-4"
                >
                  <p className="text-sm font-semibold text-brand">Add category</p>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                    <label className="block min-w-0 flex-1 text-sm">
                      <span className="text-brand-muted">Title</span>
                      <input
                        value={newTitle}
                        onChange={(e) => setNewTitle(e.target.value)}
                        placeholder="e.g. New category"
                        className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                      />
                    </label>
                    <div className="sm:w-64">
                      <IconPicker
                        value={newIcon}
                        onChange={setNewIcon}
                        disabled={busy}
                      />
                    </div>
                    <label className="block text-sm sm:w-52">
                      <span className="text-brand-muted">Type</span>
                      <select
                        value={newKind}
                        onChange={(e) => {
                          const kind = e.target.value
                          setNewKind(kind)
                          if (kind === 'table' && toApiFields(newColumns).length === 0) {
                            setNewColumns(defaultTableFields())
                          }
                        }}
                        className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                      >
                        <option value="rich">Blank page (rich text)</option>
                        <option value="table">Table</option>
                      </select>
                    </label>
                    <button
                      type="submit"
                      disabled={busy || !newTitle.trim()}
                      className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
                    >
                      Add
                    </button>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-brand">
                    <input
                      type="checkbox"
                      checked={newIsSub}
                      disabled={busy || newParentChoices.length === 0}
                      onChange={(e) => {
                        const on = e.target.checked
                        setNewIsSub(on)
                        if (!on) setNewParentSlug('')
                        else if (!newParentSlug && newParentChoices[0]) {
                          setNewParentSlug(newParentChoices[0].slug)
                        }
                      }}
                    />
                    Sub-category in the menu
                  </label>
                  {newIsSub && (
                    <label className="block max-w-md text-sm">
                      <span className="text-brand-muted">Parent category</span>
                      <select
                        value={newParentSlug}
                        onChange={(e) => setNewParentSlug(e.target.value)}
                        disabled={busy}
                        className="mt-1 w-full rounded border border-slate-300 bg-white px-3 py-2"
                      >
                        <option value="">Select parent…</option>
                        {newParentChoices.map((p) => (
                          <option key={p.slug} value={p.slug}>
                            {p.title}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  {newKind === 'table' && (
                    <FieldEditor
                      columns={newColumns}
                      onChange={setNewColumns}
                      disabled={busy}
                    />
                  )}
                </form>
              </div>
            )}
          </div>
    </SettingsShell>
  )
}
